<script setup lang="ts">
import type { TimeoutLimitsPayload } from '@/types/UserLimits';

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { setCurrentMenuOpenByGroup } = useAccountMenuStore();
const { openToaster } = useLayoutStore();
const { isAccountTimeoutLimitsEnabled } = useFeatureFlagsNew();
const { getDateTime } = useDateTimeHelper();
const { fetchUserProfile } = useUserData();

const { userLimits, setTimeOutLimit, userTimeoutMinOptionVal } =
  useUserLimits();

useSeo({
  title: t('account.timeout_limits.title'),
});

const isLoading = ref<boolean>(false);
const timeOutActive = ref<number>(0);
const timeOutPeriod = ref<string>('0');

const choiceOptions = [
  { value: 0, label: t('general.no') },
  { value: 1, label: t('general.yes') },
];

const timeOutOptions = [
  { value: '0', label: t('account.timeout_limits.select_period') },
  { value: '1', label: `1 ${t('general.day')}` },
  ...Array.from({ length: 44 }, (_, i) => ({
    value: (i + 2).toString(),
    label: `${i + 2} ${t('general.days')}`,
  })),
];

const reasonOptions = [
  { value: '0', label: t('account.timeout_limits.reason_0') },
  { value: '1', label: t('account.timeout_limits.reason_1') },
  { value: '2', label: t('account.timeout_limits.reason_2') },
  { value: '3', label: t('account.timeout_limits.reason_3') },
  { value: '4', label: t('account.timeout_limits.reason_4') },
  { value: '5', label: t('account.timeout_limits.reason_5') },
  { value: '6', label: t('account.timeout_limits.reason_6') },
];

const timeOutReason = ref<string>('0');

const timeOutOptionsFiltered = computed(() => {
  return timeOutOptions.filter(
    (option) =>
      Number(option.value) >= Number(userTimeoutMinOptionVal.value) ||
      option.value === '0',
  );
});

async function handleSubmit() {
  if (timeOutPeriod.value === '0' || !timeOutPeriod.value) {
    openToaster({
      message: t('account.timeout_limits.select_period'),
      type: 'error',
      time: 10000,
    });

    return;
  }

  if (timeOutReason.value === '0' || !timeOutReason.value) {
    openToaster({
      message: t('account.timeout_limits.reason_0'),
      type: 'error',
      time: 10000,
    });

    return;
  }

  await $containment.handle('update_limits', () => {
    sendSubmit();
  });
}

async function sendSubmit() {
  if (!Number(timeOutPeriod.value)) {
    return;
  }

  if (!Number(timeOutReason.value)) {
    return;
  }

  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  isLoading.value = true;

  const payload: TimeoutLimitsPayload = {
    user_pause: Number(timeOutPeriod.value),
    reason: Number(timeOutReason.value),
  };

  if ($kyc?.current?.id) {
    payload.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    payload.password_check = temporaryPassword.get();
  }

  await setTimeOutLimit(payload)
    .then((res: any) => {
      const message =
        res.data?.message || t('account.timeout_limits.update_success');

      openToaster({
        message,
        type: 'success',
      });
    })
    .catch((error: any) => {
      const message =
        error.data?.message ||
        error.message ||
        t('account.timeout_limits.generic_error');

      openToaster({
        message,
        type: 'error',
        time: 12000,
      });
    })
    .finally(() => {
      isLoading.value = false;
      temporaryPassword.clear();
    });
}

const isDisabledRemoveTimeOut = computed(() => {
  if (userLimits.value?.user_pause) {
    return true;
  }

  return false;
});

const isDisabledTimeOutButton = computed(() => {
  if (
    isLoading.value ||
    !timeOutPeriod.value ||
    !timeOutReason.value ||
    timeOutPeriod.value === '0' ||
    timeOutReason.value === '0'
  ) {
    return true;
  }

  return false;
});

function getTimeOutButtonText() {
  if (timeOutPeriod.value === '0') {
    return t('account.timeout_limits.select_period');
  }

  if (timeOutReason.value === '0') {
    return t('account.timeout_limits.reason_0');
  }

  if (timeOutPeriod.value === '1') {
    return t('account.timeout_limits.pause_for_1_day');
  }

  return t('account.account_protection.button_save', {
    period: timeOutPeriod.value,
  });
}

const setUserLimits = () => {
  if (userLimits.value?.user_pause) {
    timeOutActive.value = 1;
    timeOutPeriod.value = String(userLimits.value?.user_pause);
  } else {
    timeOutActive.value = 0;
  }
};

onBeforeMount(async () => {
  if (!userLimits.value?.user_pause_at) {
    await fetchUserProfile({ force: true });
  }
});

onMounted(() => {
  setCurrentMenuOpenByGroup('user-account');
  setUserLimits();
});
</script>

<template>
  <div
    v-if="!isAccountTimeoutLimitsEnabled"
    class="alert alert-error mb-6"
  >
    {{ $t('error.page_not_found.subtitle') }}
  </div>

  <section v-else>
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <div :class="$style.formContainer">
      <div :class="$style.groupRadio">
        <label>
          {{ $t('account.timeout_limits.set_timeout') }}
        </label>
        <BaseFormRadio
          v-model="timeOutActive"
          :disabled="isDisabledRemoveTimeOut"
          :options="choiceOptions"
        />
      </div>

      <template v-if="timeOutActive !== 0">
        <div :class="$style.formGroup">
          <label for="dailyLimit">
            {{ $t('account.timeout_limits.pause_for') }}
          </label>
          <BaseFormSelectField
            id="fieldType"
            v-model="timeOutPeriod"
            :initial-value="timeOutPeriod"
            name="fieldType"
            :options="timeOutOptionsFiltered"
          />
        </div>

        <div :class="$style.formGroup">
          <label for="fieldReason">
            {{ $t('account.timeout_limits.reason') }}
          </label>
          <BaseFormSelectField
            id="fieldReason"
            v-model="timeOutReason"
            :initial-value="timeOutReason"
            name="fieldReason"
            :options="reasonOptions"
          />
        </div>
      </template>

      <div
        v-if="userLimits?.user_pause"
        :class="$style.lastChange"
      >
        {{ $t('account.account_protection.last_change') }}
        <strong>
          {{ getDateTime(userLimits?.user_pause_at) }}
        </strong>
      </div>

      <div
        v-if="timeOutActive"
        class="flex justify-between items-end w-full col-span-2"
      >
        <button
          :class="$style.btnSubmit"
          :disabled="isDisabledTimeOutButton"
          @click="handleSubmit"
        >
          <NuxtIcon name="check" />
          {{ getTimeOutButtonText() }}
        </button>
      </div>
    </div>
  </section>
</template>

<style lang="scss" module>
.formContainer {
  @apply w-full flex flex-col gap-4 mt-5 pt-5 border-t border-texts/10 border-solid;

  .groupRadio {
    @apply flex items-center gap-5 font-semibold text-xs md:text-sm text-texts;
  }

  .formGroup {
    @apply relative;

    label {
      @apply text-xs font-medium;
    }
  }

  .lastChange {
    @apply text-2xs text-warning/70;
  }
}

.btnSubmit {
  @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2;

  &[disabled]:not([disabled='false']) {
    @apply opacity-40 cursor-not-allowed;
  }
}
</style>
